// A sheet as its owner fills it in: the template's sections, a row per line. A row with a scale
// is its pick and a remark beside it; nothing is required to save. The kinds are rows.js's.

import { ANSWERS } from './rows.js';

const toggle = (list, code) =>
  list.includes(code) ? list.filter((c) => c !== code) : [...list, code];

function Remark({ row, value, onChange }) {
  return (
    <input
      className="credit-remark"
      aria-label={`${row.label} remark`}
      placeholder="Remark"
      value={value.remark}
      onChange={(event) => onChange({ ...value, remark: event.target.value })}
    />
  );
}

function Pick({ label, list, value, onChange }) {
  return (
    <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">—</option>
      {list.map((option) => (
        <option key={option.code} value={option.code}>{option.label}</option>
      ))}
    </select>
  );
}

function Control({ row, value, onChange }) {
  switch (row.kind) {
    case 'check':
      return (
        <>
          <span className="sheet-check">
            <label>
              Received
              <Pick label={`${row.label} received`} list={ANSWERS} value={value.received}
                    onChange={(received) => onChange({ ...value, received })} />
            </label>
            <label>
              Verified
              <Pick label={`${row.label} verified`} list={ANSWERS} value={value.verified}
                    onChange={(verified) => onChange({ ...value, verified })} />
            </label>
          </span>
          <input
            className="credit-remark"
            aria-label={`${row.label} comment`}
            placeholder="Comment"
            value={value.comment}
            onChange={(event) => onChange({ ...value, comment: event.target.value })}
          />
        </>
      );
    case 'pick':
      return <Pick label={row.label} list={row.options} value={value} onChange={onChange} />;
    case 'date':
      return (
        <input type="date" aria-label={row.label} value={value}
               onChange={(event) => onChange(event.target.value)} />
      );
    case 'grade':
      return (
        <>
          <Pick label={row.label} list={row.options} value={value.grade}
                onChange={(grade) => onChange({ ...value, grade })} />
          <Remark row={row} value={value} onChange={onChange} />
        </>
      );
    case 'choices':
      return (
        <>
          <span className="credit-choices" role="group" aria-label={row.label}>
            {row.options.map((option) => (
              <label key={option.code} className="choice">
                <input
                  type="checkbox"
                  checked={value.choices.includes(option.code)}
                  onChange={() => onChange({ ...value, choices: toggle(value.choices, option.code) })}
                />
                {option.label}
              </label>
            ))}
          </span>
          <Remark row={row} value={value} onChange={onChange} />
        </>
      );
    case 'limit':
      return (
        <>
          <span className="credit-amount">
            <input
              className="credit-currency"
              aria-label={`${row.label} currency`}
              maxLength={3}
              value={value.currency}
              onChange={(event) => onChange({ ...value, currency: event.target.value })}
            />
            <input
              aria-label={row.label}
              inputMode="decimal"
              placeholder="250,000"
              value={value.amount}
              onChange={(event) => onChange({ ...value, amount: event.target.value })}
            />
          </span>
          <Remark row={row} value={value} onChange={onChange} />
        </>
      );
    case 'tenor':
      return (
        <>
          <span className="credit-amount">
            <input
              aria-label={row.label}
              inputMode="numeric"
              placeholder="Days"
              value={value.days}
              onChange={(event) => onChange({ ...value, days: event.target.value })}
            />
          </span>
          <Remark row={row} value={value} onChange={onChange} />
        </>
      );
    case 'review':
      return (
        <>
          <input
            aria-label={`${row.label} name`}
            placeholder="Name"
            value={value.name}
            onChange={(event) => onChange({ ...value, name: event.target.value })}
          />
          <input
            type="date"
            aria-label={`${row.label} date`}
            value={value.reviewed_on}
            onChange={(event) => onChange({ ...value, reviewed_on: event.target.value })}
          />
        </>
      );
    case 'longtext':
      return (
        <textarea className="credit-wide" aria-label={row.label} value={value}
                  onChange={(event) => onChange(event.target.value)} />
      );
    default:
      return (
        <input className="credit-wide" aria-label={row.label} value={value}
               onChange={(event) => onChange(event.target.value)} />
      );
  }
}

// `waiting` names the rows approval is still waiting on, as last saved.
export default function SheetFields({ sections, draft, onChange, errors, waiting }) {
  return sections.map((section) => (
    <fieldset key={section.title}>
      <legend>{section.title}</legend>
      {section.rows.map((row) => (
        <div key={row.name} className="credit-row">
          <span className="field-label">
            {row.label}
            {waiting.includes(row.name) && <span className="credit-waiting"> · needed to approve</span>}
          </span>
          <Control
            row={row}
            value={draft[row.name]}
            onChange={(value) => onChange((prev) => ({ ...prev, [row.name]: value }))}
          />
          {errors[row.name] && <span className="field-error">{errors[row.name]}</span>}
        </div>
      ))}
    </fieldset>
  ));
}
